import { useQueryClient } from '@tanstack/react-query';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { scanError, useStartScan } from './site.js';
import { Button } from './ui.js';

/**
 * "Check the whole site again" (owner's request, 25.09): availability, SSL, domain, DNS, e-mail, the
 * home page, speed and – once ownership is confirmed – all pages and links, right now. Counts as one
 * manual scan of the plan's daily limit.
 */
export function FullRescan({
  siteId,
  panel,
  card,
}: {
  siteId: string;
  /**
   * The dashboard's score card (site-dashboard.html .rescan): the button, the last check and how many
   * manual checks the plan allows, right-aligned beside the grade.
   */
  panel?: { lastCheck: string | null; limit: string | null };
  /**
   * Otherwise a card of its own (site-area.html): what it checks, how many manual checks the plan
   * allows a day, the button beside it and below it what the page adds.
   */
  card?: { perDay: number | null; children?: ReactNode };
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const titleId = useId();
  const scan = useStartScan(siteId, 'full');
  const result = scan.data;
  const start = () =>
    scan.mutate(undefined, {
      // Most checks finish within a minute; the crawl and speed test take a few more.
      onSuccess: () =>
        setTimeout(() => void client.invalidateQueries({ queryKey: ['site', siteId] }), 30_000),
    });
  const messages = (
    <>
      {result ? (
        <p role="status">
          {result.crawl === false ? (
            <>
              {t('rescan.queuedNoCrawl')}{' '}
              <Link to={`/sites/${siteId}/settings`}>{t('rescan.verify')}</Link>
            </>
          ) : (
            t('rescan.queued')
          )}
        </p>
      ) : null}
      {scan.isError ? <p role="alert">{scanError(scan.error, t)}</p> : null}
    </>
  );
  const button = (
    <Button
      variant="secondary"
      size="sm"
      title={panel ? t('rescan.hint') : undefined}
      busy={scan.isPending}
      disabled={scan.isSuccess}
      onClick={start}
    >
      {t('rescan.button')}
    </Button>
  );

  if (panel)
    return (
      <div className="rescan">
        {button}
        {panel.lastCheck ? <span>{panel.lastCheck}</span> : null}
        {panel.limit ? <span>{panel.limit}</span> : null}
        {messages}
      </div>
    );

  return (
    <section className="card recheck" aria-labelledby={titleId}>
      <div className="hd">
        <div>
          <h2 id={titleId}>{t('rescan.title')}</h2>
          <p className="bodyM">
            {t('rescan.hint')}
            {card?.perDay ? ` ${t('rescan.perDayNote', { count: card.perDay })}` : null}
          </p>
        </div>
        {button}
      </div>
      {messages}
      {card?.children}
    </section>
  );
}
